import React, { useState, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Eye, Network, BarChart3, Zap } from 'lucide-react';

const NeuralNetworkModelLazy = React.lazy(() => import('../components/NeuralNetworkModel'));

const Research = () => {
  const [modelMode, setModelMode] = useState('');
  return (
    <div>
      <section className="relative py-20 bg-gradient-to-br from-deepPurple/20 to-electricBlue/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-neonGreen font-semibold mb-4">AI, technology, and a little curiosity</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">The <span className="text-electricBlue">Lab</span></h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">Our space to experiment with AI and emerging technology, build prototypes, and share what we learn. This is the side passion that gives Imagination Labs its name.</p>
        </div>
      </section>
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold mb-8">Questions that keep us curious</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            ['AI and language', 'How can models help us work with information? We’re interested in language, generation, and the limits of what AI understands.'],
            ['Creative technology', 'What happens when new tools meet an unexpected idea? A place for prototypes, visual experiments, and playful exploration.'],
            ['How systems learn', 'Neural networks, computer vision, and learning through interaction give us plenty to investigate and explain.'],
          ].map(([title, description]) => (
            <article key={title} className="bg-gradient-to-br from-darkGrey to-darkPurple p-6 rounded-xl border border-deepPurple/30">
              <h3 className="text-xl font-bold mb-3 text-electricBlue">{title}</h3>
              <p className="text-gray-300">{description}</p>
            </article>
          ))}
        </div>
        <p className="text-gray-400 mt-8">The visualization below is an educational experiment, not a benchmark or a production AI system.</p>
      </section>
      {/* 3D Model Visualization Section */}
      <section className="py-16 bg-darkGrey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-3xl font-bold mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-white">Explore an </span>
            <span className="text-electricBlue">AI Model</span>
          </motion.h2>

          <div className="card p-6 md:p-8">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-4 text-white">Neural Network Architecture</h3>
                <p className="text-gray-300 mb-4">
                  An interactive illustration of a neural network, built for exploration and learning.
                  Rotate, zoom, and explore different layers to understand how connected layers can represent information flow.
                </p>
                <div className="space-y-4 mt-6">
                  <button
                    onClick={() => setModelMode(prev => prev === 'attention' ? '' : 'attention')}
                    className={`w-full text-left px-4 py-3 rounded-lg border ${modelMode === 'attention' ? 'border-electricBlue bg-electricBlue/20' : 'border-deepPurple/30 bg-deepPurple/20'} text-white hover:bg-deepPurple/30 transition-colors flex justify-between items-center`}
                  >
                    <span>Show Attention Mechanisms</span> <Eye size={16} />
                  </button>
                  <button
                    onClick={() => setModelMode(prev => prev === 'dataflow' ? '' : 'dataflow')}
                    className={`w-full text-left px-4 py-3 rounded-lg border ${modelMode === 'dataflow' ? 'border-electricBlue bg-electricBlue/20' : 'border-deepPurple/30 bg-deepPurple/20'} text-white hover:bg-deepPurple/30 transition-colors flex justify-between items-center`}
                  >
                    <span>Visualize Data Flow</span> <Network size={16} />
                  </button>
                  <button
                    onClick={() => setModelMode(prev => prev === 'compare' ? '' : 'compare')}
                    className={`w-full text-left px-4 py-3 rounded-lg border ${modelMode === 'compare' ? 'border-electricBlue bg-electricBlue/20' : 'border-deepPurple/30 bg-deepPurple/20'} text-white hover:bg-deepPurple/30 transition-colors flex justify-between items-center`}
                  >
                    <span>Explore Another Path</span> <BarChart3 size={16} />
                  </button>
                </div>
                <div className="mt-6 p-4 bg-deepPurple/20 rounded-lg border border-deepPurple/30">
                  <p className="text-gray-300 text-sm">
                    <span className="text-neonGreen font-medium">Pro Tip:</span> Click on any neuron in the model to see its connections and details. You can rotate, zoom, and pan the model using your mouse.
                  </p>
                </div>
              </div>
              <div className="w-full md:flex-1 h-[400px] bg-gradient-to-br from-deepPurple/30 to-electricBlue/20 rounded-xl border border-deepPurple/30 overflow-hidden">
                {/* Wrap with Suspense for lazy loading */}
                <Suspense fallback={<ModelLoadingPlaceholder />}>
                  <NeuralNetworkModelLazy mode={modelMode} />
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const ModelLoadingPlaceholder = () => (
  <div className="h-full w-full flex flex-col items-center justify-center p-8">
    <Zap size={60} className="text-electricBlue mb-4 animate-pulse" />
    <p className="text-white text-lg mb-2">Loading 3D Model...</p>
    <div className="w-48 h-2 bg-deepPurple/20 rounded-full overflow-hidden mt-2">
      <motion.div
        className="h-full bg-gradient-to-r from-electricBlue to-neonGreen"
        initial={{ width: "0%" }}
        animate={{ width: "100%" }}
        transition={{ duration: 1.5, repeat: Infinity }}
      />
    </div>
  </div>
);


export default Research;
